import { Link } from 'react-router-dom';
import Breadcrumb from '../../../components/feature/Breadcrumb';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';
import FilamentComparePanel from '../../../components/feature/FilamentComparePanel';
import { FilamentStressStrainChart } from '../../../components/feature/FilamentStressStrainChart';
import { useFilamentText } from '../../../hooks/useFilamentText';
import BendingStrengthCard from '../../../components/feature/BendingStrengthCard';

const ASAPage = () => {
  const { isEnglish, getMaterialData, basicInfo, recommendedSettings, derivatives, useCases, precautions, classification, advanced, baseResin, item, recommendedValue, notes, nozzleTemp, bedTemp, fanSetting, printSpeed, nozzleDiameter, caution } = useFilamentText('asa');
  const materialData = getMaterialData('asa');

  const breadcrumbItems = [
    { label: isEnglish ? 'Home' : 'ホーム', path: isEnglish ? '/en' : '/' },
    { label: isEnglish ? 'Filament Data' : 'フィラメントデータ', path: isEnglish ? '/en/filament' : '/filament' },
    { label: 'ASA', path: isEnglish ? '/en/filament/asa' : '/filament/asa' }
  ];

  const filamentId = 'asa';

  return (
    <>
      <Header />
      <div className="min-h-screen bg-graphite mechanical-grid">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <Breadcrumb items={breadcrumbItems} />
          
          <div className="space-y-8 mt-8" data-lang={isEnglish ? 'en' : 'ja'}>
            {/* タイトル */}
            <div className="text-center">
              <h1 className="text-4xl font-bold text-white font-['Exo'] mb-4">
                ASA
              </h1>
              <div className="section-divider max-w-md mx-auto"></div>
            </div>

            {/* 比較指標 */}
            <FilamentComparePanel slug="asa" />

            {/* 基本情報 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {basicInfo}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <div className="space-y-3">
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{classification}</span>
                      <span className="difficulty-advanced px-3 py-1 rounded text-sm font-semibold">{advanced}</span>
                    </div>
                    <div className="flex items-center space-x-3">
                      <span className="text-metal-light">{baseResin}</span>
                      <span className="text-white">{materialData?.baseResin}</span>
                    </div>
                  </div>
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">{isEnglish ? 'Key Features' : '主な特徴'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.features.map((feature, idx) => (
                      <li key={idx}>• {feature}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>

            {/* 推奨設定 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {recommendedSettings}
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-[#3a3d42] text-white">
                      <th className="px-4 py-3 text-left">{item}</th>
                      <th className="px-4 py-3 text-left">{recommendedValue}</th>
                      <th className="px-4 py-3 text-left">{notes}</th>
                    </tr>
                  </thead>
                  <tbody className="text-metal-light">
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{nozzleTemp}</td>
                      <td className="px-4 py-3 text-white">240-270°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Slightly higher than ABS' : 'ABSより若干高温'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{bedTemp}</td>
                      <td className="px-4 py-3 text-white">90-110°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Prevent warping' : '反り防止'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{fanSetting}</td>
                      <td className="px-4 py-3 text-white">0-20%</td>
                      <td className="px-4 py-3">{isEnglish ? 'Prioritize layer adhesion' : '層間接着優先'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{printSpeed}</td>
                      <td className="px-4 py-3 text-white">40-60mm/s</td>
                      <td className="px-4 py-3">{isEnglish ? 'Quality focused' : '品質重視'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{nozzleDiameter}</td>
                      <td className="px-4 py-3 text-white">0.4-0.6mm</td>
                      <td className="px-4 py-3">{isEnglish ? 'Standard setting' : '標準設定'}</td>
                    </tr>
                    <tr className="border-b border-metal">
                      <td className="px-4 py-3">{isEnglish ? 'Chamber Temperature' : 'チャンバー温度'}</td>
                      <td className="px-4 py-3 text-white">40-60°C</td>
                      <td className="px-4 py-3">{isEnglish ? 'Enclosed chamber recommended' : '密閉チャンバー推奨'}</td>
                    </tr>
                    <tr>
                      <td className="px-4 py-3">{caution}</td>
                      <td className="px-4 py-3 text-white" colSpan={2}>{isEnglish ? 'Ventilation required, less odor than ABS' : '換気必要、ABSより臭気少ない'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            {/* 応力-ひずみグラフ（CSV自動探索） */}
            {/* 曲げ強度 */}
            <BendingStrengthCard slug={filamentId} isEnglish={isEnglish} />

            <FilamentStressStrainChart 
              filamentId={filamentId}
              filamentName="ASA"
            />

            {/* 派生・バリエーション */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {derivatives}
              </h2>
              <p className="text-metal-light mb-6">
                {materialData?.derivatives}
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                <div className="bg-metal p-4 rounded border-l-2 border-accent">
                  <h3 className="text-white font-semibold mb-2">ASA</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Standard ASA. Improved ABS with excellent weather and UV resistance.' : '標準ASA。ABSの改良版、優れた耐候性とUV耐性。'}</p>
                </div>
                <Link 
                  to={isEnglish ? '/en/filament/asa-cf' : '/filament/asa-cf'}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">ASA-CF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Carbon fiber reinforced. Ideal for high-strength outdoor parts.' : 'カーボン繊維強化。屋外用途での高強度部品に最適。'}</p>
                </Link>
                <Link 
                  to={isEnglish ? '/en/filament/asa-gf' : '/filament/asa-gf'}
                  className="bg-metal p-4 rounded hover:bg-[#3a3d42] transition-colors border-l-2 border-accent group"
                >
                  <h3 className="text-white font-semibold mb-2 group-hover:text-accent transition-colors">ASA-GF</h3>
                  <p className="text-metal-light text-sm">{isEnglish ? 'Glass fiber reinforced. Combines dimensional stability and weather resistance.' : 'ガラス繊維強化。寸法安定性と耐候性を両立。'}</p>
                </Link>
              </div>
            </div>

            {/* 使用例 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {useCases}
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Suitable Applications' : '向いている造形'}</h3>
                  <ul className="text-metal-light space-y-1 text-sm">
                    {materialData?.suitableUses.map((use, idx) => (
                      <li key={idx}>• {use}</li>
                    ))}
                  </ul>
                </div>
                <div>
                  <h3 className="text-white font-semibold mb-2">{isEnglish ? 'Pros & Cons' : '長所・短所'}</h3>
                  <div className="space-y-2 text-sm">
                    <div>
                      <span className="text-green-400">{isEnglish ? 'Pros:' : '長所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.pros}</span>
                    </div>
                    <div>
                      <span className="text-red-400">{isEnglish ? 'Cons:' : '短所：'}</span>
                      <span className="text-metal-light"> {materialData?.proscons.cons}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* 注意事項 */}
            <div className="metal-panel p-6">
              <h2 className="text-2xl font-semibold text-white font-['Exo'] mb-4 accent-line pb-2">
                {precautions}
              </h2>
              <div className="text-metal-light space-y-2">
                {materialData?.precautions.map((precaution, idx) => (
                  <p key={idx}>• {precaution}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default ASAPage;
